import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import clsx from "clsx";
import { Grid } from "@mui/material";
import { Button, Card, DateField, Stack, Box } from "@curves/ui-base";
import { parseDate } from "@util/moment";
import { refIsRequiredError, isEqual, isFunction } from "@util/utils";
import useShowPDF from "@util/hook/useShowPDF";
import useDocHealthDeclarationApi from "@apis/useDocHealthDeclarationApi";
import { PEOPLE_TYPE, HEALTHDECLARATION_CONTRACT_VERSIONE } from "@icoach/constants/";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import HealthDeclarationContent101 from "@icoach/documents/healthDeclaration/HealthDeclarationContent101";
import HealthDeclarationContent103 from "@icoach/documents/healthDeclaration/HealthDeclarationContent103";
import FocusModeToggle from "@icoach/components/FocusModeToggle";
import { QUERY_KEYS } from "@icoach/constants/queryConstants";
import { EmployeeSelect } from "@icoach/components/";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";

const baseDatePeopleTypes = [PEOPLE_TYPE.ONLYJOIN, PEOPLE_TYPE.TRANSFERMANUALLY];

const HealthSurveyContent = React.forwardRef((props, ref) => {
	const { isReadOnly = false, onReturnHistory, onReturnContinueDoc } = props;
	const { referenceID, peopleType, contractID } = useParams();
	const history = useHistory();
	const { getDocHealthDeclarationContractApi, postDocHealthDeclarationContractApi } = useDocHealthDeclarationApi();
	const [isMounted, setMounted] = useState(false);
	const [options, setOptions] = useState({});
	const [sourceData, setSourceData] = useState({});
	const contentRef = React.useRef(null);
	const employeeRef = React.useRef(null);
	const fillDateRef = React.useRef(null);
	// 防止連點和多送一次
	const apiSendingRef = React.useRef(false);
	const showPDF = useShowPDF();
	const focusModeRef = useRef(null);

	useInactivityTimerToggle();

	const getAllParams = () => {
		let paramsPack = { ...sourceData };
		const employeeID = employeeRef.current;
		const fillDate = fillDateRef.current;
		Object.assign(paramsPack, { employeeID: employeeID.getResult() || 0 });
		Object.assign(paramsPack, { fillDate: fillDate.getResult() || parseDate(sourceData.fillDate) });
		Object.assign(paramsPack, { targetType: peopleType, targetID: referenceID });
		Object.assign(paramsPack, { survey: peopleType, targetID: referenceID });
		Object.assign(paramsPack, contentRef.current.getResult());

		return paramsPack;
	};

	// 確認送出
	const handleSendOnClick = () => {
		if (apiSendingRef.current) return false;
		if (!contentRef.current.isError() && !refIsRequiredError(employeeRef)) {
			const params = getAllParams();
			// console.log(params);
			apiSendingRef.current = true;
			addDocHealthDeclarationData(params);
		}
	};

	// 返回總覽頁
	const handleReturnHistory = () => {
		window.setTimeout(onReturnHistory, 5);
	};

	// 新增健康申告書
	const addDocHealthDeclarationData = async params => {
		const resp = await postDocHealthDeclarationContractApi(params, contractID);
		if (resp) {
			if (resp?.completedPath) {
				showPDF({
					open: true,
					pdfUrl: resp.completedPath,
					onClose: () => {
						// 新增結束後的動作判斷及處理
						if (Object.values(QUERY_KEYS).some(key => window.location.search.indexOf(key) !== -1) && isFunction(onReturnContinueDoc)) {
							onReturnContinueDoc(resp.contractID);
						}
					},
				});
			}
			window.setTimeout(() => {
				const contractID = resp.contractID;
				let path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_READONLY_PATH(peopleType, referenceID, contractID);
				path = path + window.location.search;
				history.replace(path);
			}, 5);
		} else {
			apiSendingRef.current = false;
		}
	};

	// 初始畫面資料
	const doInitDataApi = async (params, id) => {
		const resp = await getDocHealthDeclarationContractApi(params, id);
		if (resp) {
			const { resource, ...contractData } = resp;
			setOptions({ employeesOption: resource.coachOptions });
			setSourceData(contractData);
			setMounted(true);
		}
	};

	useEffect(
		() => {
			doInitDataApi(
				{
					id: referenceID,
					type: peopleType,
				},
				contractID
			);
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[contractID]
	);

	// isCompleteContract:是否為正式文件
	const { employeeID = null, fillDate = null, contractVersion } = sourceData;
	const onlyCreate = baseDatePeopleTypes.some(t => isEqual(peopleType, t));

	return (
		<FocusModeToggle ref={focusModeRef} showToggleFocusModeButton={!isReadOnly} defaultFocusMode={!isReadOnly}>
			<Card>
				{isMounted && (
					<Card.CardContent className="py-3 px-5">
						{[HEALTHDECLARATION_CONTRACT_VERSIONE.HEALTHDECLARATION2017, HEALTHDECLARATION_CONTRACT_VERSIONE.HEALTHDECLARATION2022].some(t => isEqual(contractVersion, t)) && (
							<HealthDeclarationContent101 ref={contentRef} sourceData={sourceData} peopleType={peopleType} isReadOnly={isReadOnly} />
						)}
						{isEqual(contractVersion, HEALTHDECLARATION_CONTRACT_VERSIONE.HEALTHDECLARATION2024) && (
							<HealthDeclarationContent103 ref={contentRef} sourceData={sourceData} peopleType={peopleType} isReadOnly={isReadOnly} />
						)}
						<Box className={clsx(isReadOnly && "mb-3", "p-3", "bg-light")}>
							<Grid spacing={3} container>
								<Grid xs={4} item>
									<DateField defaultValue={parseDate(fillDate)} ref={fillDateRef} label="填表日期" readOnly={isReadOnly} fullWidth required />
								</Grid>
								<Grid xs={4} item>
									<EmployeeSelect ref={employeeRef} label="擔當教練" options={options.employeesOption} defaultValue={employeeID} readOnly={isReadOnly} fullWidth required />
								</Grid>
							</Grid>
						</Box>
						<Stack className="my-3" justifyContent="center" spacing={2}>
							{!onlyCreate && (
								<Button variant="contained" color="secondary" className="btn-minWidth" onClick={handleReturnHistory}>
									返回查看歷史
								</Button>
							)}
							{!isReadOnly && (
								<React.Fragment>
									<Button variant="contained" color="secondary" className="btn-minWidth" onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSendOnClick)}>
										確認送出
									</Button>
								</React.Fragment>
							)}
						</Stack>
					</Card.CardContent>
				)}
			</Card>
		</FocusModeToggle>
	);
});

export default HealthSurveyContent;
